import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { useNavigate } from 'react-router-dom';
import { INK_COLORS, uuidv7, type Notebook } from 'shared';
import { db, saveNotebook, trashNotebook } from '../db/db.js';
import { syncer } from '../sync/sync.js';
import { useApp } from '../state.js';
import TopBar, { SyncBadge } from '../ui/TopBar.js';

export default function Library(): JSX.Element {
  const navigate = useNavigate();
  const showToast = useApp((s) => s.showToast);
  const [editing, setEditing] = useState<Notebook | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Notebook | null>(null);

  const notebooks = useLiveQuery(
    () => db.notebooks.filter((n) => !n.deletedAt).sortBy('sort'),
    [], [] as Notebook[],
  );
  const recent = useLiveQuery(
    async () => (await db.pages.filter((p) => !p.deletedAt).toArray())
      .sort((a, b) => b.updatedAt - a.updatedAt).slice(0, 8),
    [], [],
  );
  const trashCount = useLiveQuery(
    async () => (await db.notebooks.filter((n) => !!n.deletedAt).count())
      + (await db.pages.filter((p) => !!p.deletedAt).count()),
    [], 0,
  );
  const pageCount = useLiveQuery(async () => {
    const out: Record<string, number> = {};
    for (const p of await db.pages.filter((x) => !x.deletedAt).toArray()) {
      out[p.notebookId] = (out[p.notebookId] ?? 0) + 1;
    }
    return out;
  }, [], {} as Record<string, number>);

  async function createNotebook(): Promise<void> {
    const now = Date.now();
    const nb: Notebook = {
      id: uuidv7(), title: 'Novo caderno', color: INK_COLORS[0] as string,
      sort: notebooks.length, createdAt: now, updatedAt: now,
    };
    await saveNotebook(nb);
    syncer.schedule(500);
    setEditing(nb);
  }

  async function applyEdit(patch: Partial<Notebook>): Promise<void> {
    if (!editing) return;
    const next = { ...editing, ...patch, updatedAt: Date.now() };
    setEditing(next);
    await saveNotebook(next);
    syncer.schedule(1200);
  }

  async function doDelete(): Promise<void> {
    if (!confirmDelete) return;
    const n = confirmDelete.title;
    await trashNotebook(confirmDelete);
    setConfirmDelete(null);
    setEditing(null);
    syncer.schedule(500);
    showToast(`"${n}" foi para a lixeira. Dá para recuperar por 30 dias.`);
  }

  return (
    <>
      <TopBar title="Cadernos">
        <SyncBadge />
        <button onClick={() => navigate('/search')}>Buscar</button>
        <button onClick={() => navigate('/tasks')}>Tarefas</button>
        <button onClick={() => navigate('/trash')}>
          Lixeira{trashCount ? ` (${trashCount})` : ''}
        </button>
        <button onClick={() => navigate('/settings')}>Ajustes</button>
      </TopBar>

      <div className="content stack">
        <div className="grid">
          {notebooks.map((n) => (
            <div className="card notebook-card" key={n.id}>
              <span className="swatch" style={{ background: n.color }} />
              {/* O cartão inteiro abre; o ⋯ é um botão separado. Botão dentro de
                  botão não é HTML válido e o toque fica imprevisível no tablet. */}
              <button className="notebook-open" onClick={() => navigate(`/c/${n.id}`)}>
                <h3>{n.title}</h3>
                <p>{pageCount[n.id] ?? 0} página(s)</p>
              </button>
              <button className="notebook-menu ghost" title="Opções do caderno" onClick={() => setEditing(n)}>⋯</button>
            </div>
          ))}
          <button className="card" onClick={() => void createNotebook()}>
            <h3>+ Novo caderno</h3>
            <p>Martori, Odontospecial, pessoal…</p>
          </button>
        </div>

        {recent.length ? (
          <>
            <h2 style={{ margin: '8px 0 0', fontSize: 16 }}>Páginas recentes</h2>
            <div className="grid">
              {recent.map((p) => (
                <button key={p.id} className="card" onClick={() => navigate(`/p/${p.id}`)}>
                  <h3>{p.title || 'Sem título'}</h3>
                  <p>{p.pageDate}</p>
                </button>
              ))}
            </div>
          </>
        ) : (
          <p className="muted">Nenhuma página ainda. Crie um caderno para começar.</p>
        )}
      </div>

      {editing ? (
        <div className="sheet" onClick={(e) => { if (e.target === e.currentTarget) setEditing(null); }}>
          <div>
            <h2 style={{ margin: 0 }}>Caderno</h2>
            <div className="field">
              <span className="label">Nome</span>
              <input
                autoFocus value={editing.title}
                onChange={(e) => void applyEdit({ title: e.target.value })}
              />
            </div>
            <div className="field">
              <span className="label">Cor</span>
              <div className="color-grid">
                {INK_COLORS.map((c) => (
                  <button
                    key={c} className="color-dot" aria-label={c}
                    aria-pressed={editing.color.toLowerCase() === c.toLowerCase()}
                    style={{ background: c }} onClick={() => void applyEdit({ color: c })}
                  />
                ))}
              </div>
            </div>
            <hr className="divider" />
            <div className="row wrap">
              <button className="danger" onClick={() => setConfirmDelete(editing)}>Apagar caderno</button>
              <span className="spacer" />
              <button className="primary" onClick={() => setEditing(null)}>Pronto</button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmDelete ? (
        <div className="sheet" onClick={(e) => { if (e.target === e.currentTarget) setConfirmDelete(null); }}>
          <div>
            <h2 style={{ margin: 0 }}>Apagar &ldquo;{confirmDelete.title}&rdquo;?</h2>
            <p className="muted small" style={{ margin: 0 }}>
              {pageCount[confirmDelete.id]
                ? `As ${pageCount[confirmDelete.id]} página(s) deste caderno vão junto.`
                : 'Este caderno está vazio.'}
              {' '}Fica na lixeira por 30 dias e dá para recuperar a qualquer momento nesse prazo.
            </p>
            <div className="row wrap">
              <button onClick={() => setConfirmDelete(null)}>Cancelar</button>
              <span className="spacer" />
              <button className="danger" onClick={() => void doDelete()}>Mandar para a lixeira</button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
